import api from './api';
import logger from '../utils/logger';

const AuthService = {
  // Register a new user
  register: async (userData) => {
    try {
      const response = await api.post('/register', userData);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const response = await api.post('/login', credentials);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Logout user
  logout: async () => {
    try {
      // Clear local storage first for instant logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Then handle server-side logout with retry mechanism and timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      try {
        await api.post('/logout', {}, {
          signal: controller.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
      } catch (serverError) {
        // Check if it's a 401 Unauthorized error (already logged out)
        if (serverError.response && serverError.response.status === 401) {
          // This is expected if token is invalid, so we can ignore
          logger.log('User was already logged out on server');
        } else if (serverError.name === 'AbortError') {
          logger.log('Logout request timed out, but local logout succeeded');
        } else {
          // Log other errors but don't block the user from logging out
          logger.error('Server logout error:', serverError);
        }
      } finally {
        clearTimeout(timeoutId);
      }
      
      return { success: true };
    } catch (error) {
      // Even if everything fails, we've already cleared local state
      logger.error('Critical logout error:', error);
      return { success: true };
    }
  },

  // Get current user from localStorage
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return localStorage.getItem('token') !== null;
  },

  // Send verification code to phone number
  sendVerificationCode: async (phone) => {
    try {
      const response = await api.post('/direct-verification-code', { phone });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Verify phone number with code
  verifyPhoneNumber: async (phone, code, userId) => {
    try {
      console.log('Verification request payload:', { phone, code, user_id: userId });
      const response = await api.post('/verify-phone', { phone, code, user_id: userId });
      console.log('Verification response:', response.data);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Verification error:', error.response ? error.response.data : error);
      throw error.response ? error.response.data : error;
    }
  },

  // Update phone number and send new verification code
  updatePhoneNumber: async (phone, userId) => {
    try {
      console.log('Update phone request payload:', { phone, user_id: userId });
      const response = await api.post('/update-phone', { phone, user_id: userId });
      console.log('Update phone response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Update phone error:', error.response ? error.response.data : error);
      throw error.response ? error.response.data : error;
    }
  },

  // Skip phone verification (for SMS downtime)
  skipPhoneVerification: async (userId) => {
    try {
      const response = await api.post('/skip-phone-verification', { user_id: userId });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Skip verification error:', error.response ? error.response.data : error);
      throw error.response ? error.response.data : error;
    }
  },

  // Register a user (first step - without verification)
  registerInitial: async (userData) => {
    try {
      const response = await api.post('/register-initial', userData);
      
      // Log email status if available
      if (response.data.email_sent !== undefined) {
        console.log('Welcome email status:', {
          sent: response.data.email_sent,
          error: response.data.email_error
        });
      }
      
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Reset password
  resetPassword: async (resetData) => {
    try {
      const response = await api.post('/reset-password', resetData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Update user profile
  updateProfile: async (userData) => {
    try {
      const response = await api.put('/user/profile', userData);
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Google login
  googleLogin: async (tokenId) => {
    try {
      console.log('Google login - sending token to backend:', tokenId.substring(0, 20) + '...');
      console.log('Token type:', typeof tokenId);
      console.log('API base URL:', api.defaults.baseURL);
      
      const response = await api.post('/auth/google', { token: tokenId });
      console.log('Google login - success response:', response.data);
      
      if (response.data.token) {
        console.log('Setting auth token and user in localStorage');
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } else {
        console.warn('No token received in response:', response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Google login - error details:', error);
      console.error('Response status:', error.response?.status);
      console.error('Response data if available:', error.response?.data);
      console.error('Error stack:', error.stack);
      throw error.response ? error.response.data : error;
    }
  },

  // Apple login
  appleLogin: async (appleData) => {
    try {
      console.log('Apple login - sending data to backend:', appleData);
      console.log('API base URL:', api.defaults.baseURL);
      
      // Send the data directly as the backend expects identity_token and user fields
      const response = await api.post('/auth/apple', appleData);
      console.log('Apple login - success response:', response.data);
      
      if (response.data.token) {
        console.log('Setting auth token and user in localStorage');
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data;
      } else {
        console.warn('No token received in response:', response.data);
        throw new Error('No authentication token received from server');
      }
    } catch (error) {
      console.error('Apple login - error details:', error);
      console.error('Response status:', error.response?.status);
      console.error('Response data if available:', error.response?.data);
      console.error('Error stack:', error.stack);
      throw error.response ? error.response.data : error;
    }
  }
};

export default AuthService;
